import { format } from 'date-fns';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { State } from 'redux/initialState';
import { getBeneficiariesConsolidationPeriods } from 'utils/consolidationPeriods';
import {
  getActualSociety,
  getBeneficiariesSummaryByPeriod,
  getPlansSummaryByPeriod,
} from 'utils/filters';

import operationTypes from 'constants/operationTypes';
import periodTypes from 'constants/periodTypes';

import { getStrikePrice } from 'modules/beneficiaries/pages/BeneficiaryList/utils/beneficiaryPlanCalculations';
import {
  getBeneficiariesMixedSummary,
  getBeneficiariesSummary,
  getPlansSummary,
} from '../utils';
import modeOptions from '../utils/modeOptions';
import viewOptions from '../utils/viewOptions';

const buttons = {
  plansPeriods: 'plansPeriodsBtn',
  plansSummary: 'plansSummaryBtn',
  beneficiariesPeriods: 'beneficiariesPeriodsBtn',
  beneficiariesSummary: 'beneficiariesSummaryBtn',
};

type Params = {
  societyId: string;
};

interface I18n {
  language: string;
  [key: string]: any;
}

export const usePlanSummary = (i18n: I18n) => {
  const { societyId } = useParams<Params>();

  const user = useSelector((state: State) => state.user);
  const plans = useSelector((state: State) => state.plans);
  const tenderOffers = useSelector((state: State) => state.tenderOffers);
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [consolidationPeriods, setConsolidationPeriods] = useState<any>();
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // objects for plans summary
  const [summary, setSummary] = useState<any>();
  const [todaySummary, setTodaySummary] = useState();
  const [plansNames, setPlansNames] = useState<Record<string, string>>();
  const [values, setValues] = useState<any>();
  const [periods, setPeriods] = useState<string[]>();

  // objects for beneficiaries summary
  const [beneficiaries, setBeneficiaries] = useState<any[]>([]);
  const [beneficiariesSummary, setBeneficiariesSummary] = useState<any>();
  const [beneficiariesNames, setBeneficiariesNames] =
    useState<Record<string, string>>();
  const [beneficiariesEmails, setBeneficiariesEmails] =
    useState<Record<string, string>>();
  const [beneficiariesCifs, setBeneficiariesCifs] =
    useState<Record<string, string>>();
  const [beneficiariesStrikePrices, setBeneficiariesStrikePrices] =
    useState<Record<string, string>>();
  const [beneficiariesValues, setBeneficiariesValues] = useState<any>();
  const [beneficiariesPeriods, setBeneficiariesPeriods] = useState<string[]>();

  const [view, setView] = useState(2);
  const [mode, setMode] = useState(0);
  const [showValuesFormatted, setShowValuesFormatted] = useState(true);

  // Memoized options to avoid unnecessary recalculations
  const currentViewOptions = useMemo(() => viewOptions(i18n), [i18n]);
  const currentModeOptions = useMemo(() => modeOptions(i18n), [i18n]);

  // Check if data is loading
  const isLoading = !actualSociety;

  // Check if there's an error (can be expanded based on error handling needs)
  const isError = false;

  // Memoized handler functions
  const handleExportTable = useCallback((mode: number, view: number) => {
    let button: string | undefined;
    if (mode === 0) {
      button = view < 2 ? buttons.plansPeriods : buttons.plansSummary;
    } else if (mode === 1) {
      button =
        view < 2 ? buttons.beneficiariesPeriods : buttons.beneficiariesSummary;
    } else if (mode === 2) {
      button =
        view < 2 ? buttons.beneficiariesPeriods : buttons.beneficiariesSummary;
    }
    if (button) {
      const btn = document.getElementById(button);
      if (btn) {
        btn.click();
      }
    }
  }, []);

  const getSummaryByPeriod = useCallback(
    (mode: number, period: any) => {
      if (mode === 0) {
        return getPlansSummaryByPeriod(
          beneficiaries,
          plans,
          consolidationPeriods,
          tenderOffers,
          period
        );
      }
      return getBeneficiariesSummaryByPeriod(
        beneficiaries,
        plans,
        consolidationPeriods,
        tenderOffers,
        period
      );
    },
    [beneficiaries, plans, consolidationPeriods, tenderOffers]
  );

  const getSummary = useCallback(
    (mode: number, date: string) => {
      if (mode === 0) {
        return getPlansSummary({
          beneficiaries,
          plans,
          consolidationPeriods,
          tenderOffers,
          endDate: new Date(date),
        });
      }
      if (mode === 1) {
        return getBeneficiariesMixedSummary({
          beneficiaries,
          plans,
          consolidationPeriods,
          tenderOffers,
          endDate: new Date(date),
        });
      }
      if (mode === 2) {
        return getBeneficiariesSummary({
          beneficiaries,
          plans,
          consolidationPeriods,
          tenderOffers,
          endDate: new Date(date),
        });
      }
    },
    [beneficiaries, plans, consolidationPeriods, tenderOffers]
  );

  const getActualSummary = useCallback(() => {
    let currentSummary;

    if (view === 0) {
      currentSummary = getSummaryByPeriod(mode, periodTypes.QUARTERLY.value);
    } else if (view === 1) {
      currentSummary = getSummaryByPeriod(mode, periodTypes.ANUALLY.value);
    } else if (view === 2) {
      currentSummary = getSummary(mode, endDate);

      const newTodaySummary = getSummary(
        mode,
        format(new Date(), 'yyyy-MM-dd')
      );
      setTodaySummary(newTodaySummary);
    }

    return currentSummary;
  }, [view, mode, getSummaryByPeriod, getSummary, endDate]);

  // Effect: Get actual society
  useEffect(() => {
    if (societyId && actualSociety?.['_id'] !== societyId) {
      getActualSociety(user, societyId as string);
    }
  }, [actualSociety, user, societyId]);

  // Effect: Extract plans names
  useEffect(() => {
    if (plans?.length) {
      const names: Record<string, string> = {};
      plans.forEach((plan: any) => {
        names[plan['_id']] = plan?.name;
      });
      setPlansNames(names);
    }
  }, [plans]);

  // Effect: Extract beneficiaries data and filter non-draft
  useEffect(() => {
    if (!actualSociety?.beneficiaries?.length) {
      return;
    }

    const names: Record<string, string> = {};
    const emails: Record<string, string> = {};
    const cifs: Record<string, string> = {};
    const strikePrices: Record<string, string> = {};

    actualSociety.beneficiaries.forEach((beneficiary: any) => {
      names[beneficiary['_id']] = beneficiary?.name;
      emails[beneficiary['_id']] = beneficiary?.email || '-';
      cifs[beneficiary['_id']] = beneficiary?.cif || '-';
      strikePrices[beneficiary['_id']] =
        getStrikePrice(beneficiary, beneficiary?.plan, plans)
          ?.toString()
          ?.replace('.', ',') || '-';
    });

    setBeneficiariesNames(names);
    setBeneficiariesEmails(emails);
    setBeneficiariesCifs(cifs);
    setBeneficiariesStrikePrices(strikePrices);

    const nonDraftBeneficiaries = actualSociety.beneficiaries.filter(
      (beneficiary: any) => !beneficiary?.isDraft
    );
    setBeneficiaries(nonDraftBeneficiaries);
  }, [actualSociety, plans]);

  // Effect: Calculate consolidation periods
  useEffect(() => {
    if (actualSociety && plans?.length && beneficiaries.length) {
      const beneficiariesConsolidationPeriods =
        getBeneficiariesConsolidationPeriods(beneficiaries, plans);
      setConsolidationPeriods(beneficiariesConsolidationPeriods);
    }
  }, [actualSociety, plans, beneficiaries]);

  // Effect: Calculate and set summary data
  useEffect(() => {
    // Check if all required data is available
    if (
      !actualSociety ||
      !beneficiaries.length ||
      !plans.length ||
      !tenderOffers ||
      !consolidationPeriods
    ) {
      return;
    }

    const actualSummary = getActualSummary();

    // Set values and periods objects for period views (quarterly/yearly)
    if (view < 2) {
      const values: any = Object.keys(actualSummary).reduce((acc: any, id) => {
        acc[id] = Object.values(actualSummary[id] as any).reduce(
          (acc: any, curr: any) => [...acc, ...Object.values(curr)],
          []
        );
        return acc;
      }, {});

      if (mode === 0) {
        setValues(values);
      } else {
        setBeneficiariesValues(values);
      }

      if (Object.keys(actualSummary).length) {
        const firstKey = Object.keys(actualSummary)[0];
        const firstElem = (actualSummary as any)[firstKey];
        const actualPeriods = Object.keys(firstElem);

        if (mode === 0) {
          setPeriods(actualPeriods);
        } else {
          setBeneficiariesPeriods(actualPeriods);
        }
      }
    }

    // Set summary based on mode
    if (mode === 0) {
      // Plans view - group by plan type
      const summaryByType: any = Object.keys(actualSummary).reduce(
        (acc: any, planId) => {
          const plan = plans.find((p: any) => p['_id'] === planId);

          if (plan && plan.planType === operationTypes.STOCK_OPTION) {
            acc.stockOptions[planId] = (actualSummary as any)[planId];
          } else if (plan && plan.planType === operationTypes.PHANTOM_SHARE) {
            acc.phantoms[planId] = (actualSummary as any)[planId];
          } else if (plan && plan.planType === operationTypes.WARRANT) {
            acc.warrants[planId] = (actualSummary as any)[planId];
          }
          return acc;
        },
        { phantoms: {}, stockOptions: {}, warrants: {} }
      );
      setSummary(summaryByType);
    } else {
      // Beneficiaries view
      setBeneficiariesSummary(actualSummary);
    }
  }, [
    view,
    endDate,
    mode,
    plans,
    beneficiaries,
    tenderOffers,
    actualSociety,
    consolidationPeriods,
    getActualSummary,
  ]);

  // Prepare data object to return (only data, no actions)
  const data = useMemo(
    () => ({
      // View and mode state (values only)
      view,
      mode,
      showValuesFormatted,
      endDate,

      // Plans data
      summary,
      todaySummary,
      plansNames,
      values,
      periods,

      // Beneficiaries data
      beneficiariesSummary,
      beneficiariesNames,
      beneficiariesEmails,
      beneficiariesCifs,
      beneficiariesStrikePrices,
      beneficiariesValues,
      beneficiariesPeriods,

      // Options
      currentViewOptions,
      currentModeOptions,
    }),
    [
      view,
      mode,
      showValuesFormatted,
      endDate,
      summary,
      plansNames,
      values,
      periods,
      beneficiariesSummary,
      beneficiariesNames,
      beneficiariesEmails,
      beneficiariesCifs,
      beneficiariesStrikePrices,
      beneficiariesValues,
      beneficiariesPeriods,
      currentViewOptions,
      currentModeOptions,
    ]
  );

  // Prepare actions object to return
  const actions = useMemo(
    () => ({
      setView,
      setMode,
      setShowValuesFormatted,
      setEndDate,
      handleExportTable,
    }),
    [handleExportTable]
  );

  return {
    data,
    actions,
    isLoading,
    isError,
  };
};
